<script setup lang="ts">
import router from '@/router'
import { useUserInfoStore } from '@/stores/modules/user'
import { useHeatingStore } from '@/stores/modules/heating'
import { useFullscreen } from '@vueuse/core'
import AppBreadcrumb from './AppBreadcrumb.vue'

const props = defineProps<{
  isMobile?: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle-drawer'): void
}>()

const userStore = useUserInfoStore()
const heatingStore = useHeatingStore()

const handleRefresh = () => location.reload()
const { isFullscreen, toggle } = useFullscreen()

const handleCommand = (e: any) => {
  switch (e) {
    case 'logout':
      handleLogout()
      break
    case 'changePassword':
      handleChangePassword()
      break
    default:
      break
  }
}

const handleLogout = () => {
  ElMessageBox.confirm('是否要退出登录？', '退出登录', {
    confirmButtonText: '是',
    cancelButtonText: '否',
    type: 'warning'
  })
    .then(() => {
      userStore.logout().finally(() => {
        router.push('/login')
      })
    })
    .catch(() => {})
}

const handleChangePassword = () => {
  console.log('密码修改成功')
}
</script>

<template>
  <div class="app-header-wrapper">
    <div class="app-header">
      <div class="left">
        <el-icon v-if="props.isMobile" class="icon-btn" :size="22" @click="emit('toggle-drawer')">
          <Expand />
        </el-icon>
        <template v-else>
          <el-icon class="icon-btn" :size="20" @click="userStore.switchFolding">
            <Expand v-if="userStore.collapse" />
            <Fold v-else />
          </el-icon>
          <el-icon class="icon-btn" :size="20" @click="handleRefresh"><RefreshRight /></el-icon>
          <AppBreadcrumb />
        </template>
      </div>
      <div class="right">
        <!-- 设备状态统计（对标web-admin-ui.html header） -->
        <div class="header-stats">
          <span class="stat-item">
            <span class="dot online"></span>
            <span>在线 {{ heatingStore.statistics?.onlineCount ?? 0 }}</span>
          </span>
          <span class="stat-item">
            <span class="dot alarm"></span>
            <span>报警 {{ heatingStore.statistics?.alarmCount ?? 0 }}</span>
          </span>
          <span class="stat-item">
            <span class="dot offline"></span>
            <span>离线 {{ heatingStore.statistics?.offlineCount ?? 0 }}</span>
          </span>
        </div>
        <el-divider direction="vertical" />
        <!-- 操作按钮 -->
        <el-icon v-if="!props.isMobile" @click="toggle" class="icon-btn" :size="18">
          <FullScreen v-if="!isFullscreen" />
          <Aim v-else />
        </el-icon>
        <el-icon class="icon-btn" :size="18">
          <el-badge :value="heatingStore.statistics?.alarmCount ?? 0" :max="99">
            <Bell />
          </el-badge>
        </el-icon>
        <div class="user-info">
          <el-avatar :size="30" class="user-avatar">{{ userStore.user?.username?.charAt(0)?.toUpperCase() || 'U' }}</el-avatar>
          <el-dropdown class="dropdown" @command="handleCommand">
            <span class="user-dropdown-trigger">
              <span v-if="!props.isMobile">{{ userStore.user?.username || '用户' }}</span>
              <el-icon class="el-icon--right"><arrow-down /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item>个人中心</el-dropdown-item>
                <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
                <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-header-wrapper {
  @apply h-full w-full;
  background: rgba(15, 23, 42, 0.95);
  border-bottom: 1px solid rgba(59, 130, 246, 0.15);
}
.app-header {
  @apply h-full flex justify-between items-center px-4;
}
.left {
  @apply flex items-center space-x-3;
}
.right {
  @apply flex items-center space-x-3;
}
.icon-btn {
  @apply cursor-pointer;
  color: #94a3b8;
  &:hover {
    color: #60A5FA;
  }
}
.header-stats {
  @apply flex items-center space-x-5;
}
.stat-item {
  @apply flex items-center;
  font-size: 13px;
  color: #94a3b8;
  .dot {
    @apply w-2 h-2 rounded-full mr-1.5;
  }
  .dot.online {
    background: #10B981;
    box-shadow: 0 0 4px rgba(16, 185, 129, 0.5);
  }
  .dot.alarm {
    background: #F59E0B;
    box-shadow: 0 0 4px rgba(245, 158, 11, 0.5);
  }
  .dot.offline {
    background: #EF4444;
    box-shadow: 0 0 4px rgba(239, 68, 68, 0.5);
  }
}
.user-info {
  @apply flex items-center space-x-2 cursor-pointer;
}
.user-avatar {
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  color: white;
  font-size: 13px;
}
.user-dropdown-trigger {
  @apply flex items-center;
  font-size: 14px;
  color: #e2e8f0;
}
</style>
